/**
 * Review and Synthesis — Phase 4 of the 4-phase OBS appointment.
 *
 * Displays the evidence-summary tables, formulation, confidence rating, and
 * emerging differentials — the same content that was previously on the
 * "summary" view in the single-page observation.
 *
 * The "Finish Observation" action mirrors the SSI Review's "Generate Report"
 * flow:
 *   1. Marks the observation stage as completed.
 *   2. Triggers bulk catch-up generation for any Observation report sections
 *      (Criteria + Self-Report) that haven't been generated yet by the
 *      section-exit hooks during the session.
 *   3. Shows a simulated progress dialog while the Cloud Function processes.
 *   4. On completion, navigates to the Documents Dashboard where the
 *      clinician reviews and approves the 8 AI-generated paragraphs.
 *
 * The 8 generated paragraphs themselves are NOT shown on this page — they
 * live in the separate Review Documents page (RD_OBS_Autism), accessible
 * from the Documents Dashboard.
 *
 * This page does NOT trigger any LLM generation from its own edits — the
 * formulation, confidence, and differentials are clinical-judgement outputs
 * that feed the downstream MDT/DD module, not LLM inputs.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { serverTimestamp } from 'firebase/firestore';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../../components/autismModular/AppointmentChecklistControls';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import AdultObservationReview from '../components/AdultObservationReview';
import ModuleCompletionDialog, {
  CancelButton,
  GenerateReportButton,
  SummarySection,
  DialogMessage,
} from '../../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../../components/GenerateReportProgressDialog';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { getAutismOBSStepRoutes } from '../../../../utils/autismObsRouting';
import {
  shouldGenerateSelfReportSection,
  hasSelfReportNoteContent,
} from '../../../../utils/obsSelfReportGeneration';
import {
  hasAnyObservationCriterionDecision,
  listIncompleteObservationCriteria,
  listIncompleteObservationCriteriaTasks,
} from '../../../../utils/observationCriteriaCompletion';
import { ASD_CONDITION_LABEL } from '../../../../constants/autismConditions';
import { TERNARY_OPTIONS } from '../../../../components/autismReview/ConfidenceLevelSelector';
import { AUTISM_EVIDENCE_PILL_COLOURS } from '../../../../constants/autismEvidenceColors';
import { auth } from '../../../../firebase';
import useObservationStageData from '../hooks/useObservationStageData';

/** DSM A1-B4 — the criteria shown in the completion dialog evidence table. */
const OBS_CRITERION_IDS = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'B4'];

/** Compact evidence pill matching the SSI Review and ObservationEvidenceTables pattern. */
function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">&mdash;</span>;
  const style = AUTISM_EVIDENCE_PILL_COLOURS[value]
    || { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block w-[7.5rem] text-center px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${style.bg} ${style.text}`}>
      {style.label || value}
    </span>
  );
}

export default function Review() {
  const {
    patientId,
    tasks,
    navigate,
    flushStageWrites,
    isPreviewMode,
    canEdit,
    isObsV2,
    selectedTaskIds,
    allTaskData,
    adosData,
    criteriaEvidence,
    criteriaNotes,
    confidenceStyle,
    confidence,
    setConfidence,
    confidenceTernary,
    setConfidenceTernary,
    formulationConditions,
    differentialsConfirmed,
    emergingFormulation,
    setEmergingFormulation,
    confidentialNotes,
    setConfidentialNotes,
    handleToggleCondition,
    handleConfirmDifferentials,
    handleChangeDifferentials,
    updateAssessmentStage,
    reportContent,
    isAppointmentChecklistEnabled,
  } = useObservationStageData();

  const showChecklist = isAppointmentChecklistEnabled;

  // ── Completion + generation state ──────────────────────────────────────
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Criterion Mapping lets clinicians proceed with partial mapping, so the
  // completion dialog restates what is still undecided before the report is
  // generated. This is a warning, not a block. The "incomplete" signal is
  // version-specific and normalised to `{ key, label }` for a single render:
  //   • v2 — one evidence decision per DSM criterion on `criteriaEvidence`;
  //     the redesign no longer maps per task, so list the undecided criteria.
  //   • v1 — evidence is recorded per administered task, so list the tasks that
  //     still have undecided criteria (preserving the pre-redesign wording,
  //     including which criteria are missing on each task).
  const incompleteItems = useMemo(() => {
    if (isObsV2) {
      return listIncompleteObservationCriteria(criteriaEvidence).map((c) => ({
        key: c.criterionId,
        label: `${c.criterionId}. ${c.shortTitle}`,
      }));
    }
    return listIncompleteObservationCriteriaTasks(selectedTaskIds, tasks, allTaskData).map((t) => ({
      key: `task-${t.taskId}`,
      label: `Task ${t.displayNumber}. ${t.shortTitle}`
        + (t.missingCriteria.length > 0 ? ` (${t.missingCriteria.join(', ')})` : ''),
    }));
  }, [isObsV2, criteriaEvidence, selectedTaskIds, tasks, allTaskData]);

  // Hard gate: at least one criterion evidence decision must exist before the
  // clinician can finish the observation. Mirrors SSI's isFormReady pattern.
  // Handles both v2 (criteria-level) and v1 (per-task) paths via the same
  // helper already used by the Criteria generation guard in handleFinishObservation.
  const hasCriterionEvidence = useMemo(
    () => hasAnyObservationCriterionDecision(
      selectedTaskIds, tasks, allTaskData, criteriaEvidence,
    ),
    [selectedTaskIds, tasks, allTaskData, criteriaEvidence],
  );

  // Confidence level is also mandatory — the Finish button requires both
  // at least one criterion evidence decision AND a recorded confidence level.
  const isConfidenceRecorded = confidenceStyle === 'percentage'
    ? confidence !== null
    : !!confidenceTernary;

  const canFinish = hasCriterionEvidence && isConfidenceRecorded;

  const handleOpenCompletion = useCallback(async () => {
    if (!canEdit || !canFinish) return;
    await flushStageWrites('observation');
    setIsCompletionDialogOpen(true);
  }, [canEdit, canFinish, flushStageWrites]);

  /**
   * Handles the "Generate Report" click from the completion dialog.
   *
   * Mirrors the SSI Review's handleFinishSSI:
   *   1. Marks the observation stage as completed with audit metadata.
   *   2. Checks which OBS report sections (Criteria, Self-Report) have already
   *      been generated by the section-exit hooks and triggers catch-up
   *      generation for any that are missing.
   *   3. Shows the progress dialog so the clinician sees the generation status.
   */
  const handleFinishObservation = useCallback(async () => {
    setIsCompletionDialogOpen(false);

    updateAssessmentStage('observation', {
      status: 'completed',
      progress: 1,
      lastCompletedAt: serverTimestamp(),
      lastCompletedBy: auth.currentUser?.uid,
      lastCompletedByName: auth.currentUser?.displayName,
    });

    // Bulk catch-up generation: check which OBS sections have already been
    // generated and trigger generation for any that are missing. The OBS
    // module has two report sections: Criteria (the 7 DSM-5 criterion
    // paragraphs) and Self-Report (the self-report summary paragraph).
    const obsReport = reportContent?.observation || {};
    const obsGenerated = obsReport.stageSectionsGenerated || [];

    const dataReady = flushStageWrites('observation');

    // Criteria: catch up if the Criterion Mapping exit never fired. Partial
    // mapping still generates (same rule as the phase exit), but a phase with
    // no evidence decisions at all has nothing to summarise.
    if (
      !obsGenerated.includes('Criteria')
      && hasAnyObservationCriterionDecision(
        selectedTaskIds, tasks, allTaskData, criteriaEvidence,
      )
    ) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Behavioural Observation', 'Criteria', [], dataReady,
      );
    }

    // Self-Report catch-up: if Criterion Mapping's forward-to-Review exit never
    // fired the first draft (or the clinician reached Finish another way), draft
    // it here when there is content. Never invent an empty Self-Report paragraph
    // just because Finish was clicked (`hasChanges: false` blocks empty regen).
    if (shouldGenerateSelfReportSection({
      hasContent: hasSelfReportNoteContent(allTaskData, adosData),
      hasChanges: false,
      isGenerated: obsGenerated.includes('Self-Report'),
      allowInitialGeneration: true,
    })) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Behavioural Observation', 'Self-Report', [], dataReady,
      );
    }

    setIsGenerating(true);
  }, [
    patientId, updateAssessmentStage, reportContent, flushStageWrites,
    allTaskData, adosData, selectedTaskIds, tasks, criteriaEvidence,
  ]);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  const handleAfterGeneration = useCallback(() => {
    navigate(`/patient/${patientId}/adult-autism/documents`);
  }, [navigate, patientId]);

  // ── Navigation ─────────────────────────────────────────────────────────
  const handleBackToCriterionMapping = useCallback(async () => {
    await flushStageWrites('observation');
    navigate(getAutismOBSStepRoutes(patientId).criterionMapping);
  }, [flushStageWrites, navigate, patientId]);

  // ── Breadcrumb ─────────────────────────────────────────────────────────
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Behavioural Observation'],
    ['Review and Synthesis'],
  ];

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="observation" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          {isPreviewMode && <PreviewModeBanner />}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <AdultObservationReview
              tasks={tasks}
              selectedTaskIds={selectedTaskIds}
              allTaskData={allTaskData}
              adosData={adosData}
              criteriaEvidence={criteriaEvidence}
              criteriaNotes={criteriaNotes}
              canEdit={canEdit}
              confidenceStyle={confidenceStyle}
              confidence={confidence}
              confidenceTernary={confidenceTernary}
              onConfidenceChange={setConfidence}
              onConfidenceTernaryChange={setConfidenceTernary}
              differentials={formulationConditions}
              differentialsConfirmed={differentialsConfirmed}
              onToggleCondition={handleToggleCondition}
              onConfirmDifferentials={handleConfirmDifferentials}
              onChangeDifferentials={handleChangeDifferentials}
              emergingFormulation={emergingFormulation}
              onEmergingFormulationChange={setEmergingFormulation}
              confidentialNotes={confidentialNotes}
              onConfidentialNotesChange={setConfidentialNotes}
            />
          </div>

          <ModularStepFooter
            showBack
            backLabel="Criterion Mapping"
            onBack={handleBackToCriterionMapping}
            showNext={false}
            rightActions={canEdit ? (
              <button
                type="button"
                onClick={handleOpenCompletion}
                disabled={!canFinish}
                className={`flex items-center py-2 px-5 rounded-md shadow-sm whitespace-nowrap transition-all duration-200 ${
                  canFinish
                    ? 'text-white bg-primary hover:bg-primary/90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span className="font-semibold text-sm">Finish Observation</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            ) : undefined}
          />
        </div>
      </div>

      {/* ── Completion dialog: recaps decisions before generating ────────── */}
      <ModuleCompletionDialog
        open={isCompletionDialogOpen && !isGenerating}
        onOpenChange={setIsCompletionDialogOpen}
        title="Behavioural Observation Complete"
        description={<>Review your observation decisions before generating the report.</>}
        actions={
          <>
            <CancelButton onClick={() => setIsCompletionDialogOpen(false)} />
            <GenerateReportButton onClick={handleFinishObservation} />
          </>
        }
      >
        {/* Criteria evidence table — mirrors the SSI dialog's evidence
            recap, but with a single column (OBS has no childhood/adulthood
            split). Shows a dash for any undecided criterion. */}
        <SummarySection title="Criteria Evidence:">
          {OBS_CRITERION_IDS.map(id => (
            <div key={id} className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-gray-800 w-8">{id}</span>
              <EvidencePill value={criteriaEvidence[id]} />
            </div>
          ))}
        </SummarySection>

        <SummarySection title="Condition referred for:">
          <p className="text-sm text-gray-700">{ASD_CONDITION_LABEL}</p>
        </SummarySection>

        <SummarySection title="Confidence level:">
          <p className="text-sm text-gray-700">
            {confidenceStyle === 'percentage'
              ? (confidence !== null ? `${confidence}%` : <span className="italic text-gray-400">Not recorded</span>)
              : (confidenceTernary
                ? TERNARY_OPTIONS.find(o => o.value === confidenceTernary)?.label || confidenceTernary
                : <span className="italic text-gray-400">Not recorded</span>)}
          </p>
        </SummarySection>

        {/* Partial criterion mapping is allowed, but the clinician should see
            exactly what is still undecided before the report is drafted. */}
        {incompleteItems.length > 0 && (
          <SummarySection title="Criterion mapping is incomplete:">
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {incompleteItems.map((item) => (
                <li key={item.key}>{item.label}</li>
              ))}
            </ul>
            <p className="mt-2 text-sm italic text-amber-700">
              The criteria paragraphs will be drafted from the decisions recorded
              so far, so they may be incomplete.
            </p>
          </SummarySection>
        )}

        <SummarySection title="Emerging differentials under consideration:">
          {formulationConditions.length > 0 ? (
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {formulationConditions.map(c => <li key={c}>{c}</li>)}
            </ul>
          ) : (
            <p className="text-sm italic text-gray-400">No conditions selected</p>
          )}
        </SummarySection>

        {/* Emerging formulation recap — handles both structured-notes arrays
            (normaliseNotesValue output) and legacy plain strings. Always
            rendered; shows "Not provided" when empty. */}
        <SummarySection title="Emerging formulation:">
          {(() => {
            const MAX_ITEMS = 3;
            const items = Array.isArray(emergingFormulation)
              ? emergingFormulation.map(n => n?.text || '').filter(Boolean)
              : (typeof emergingFormulation === 'string' && emergingFormulation.trim() ? [emergingFormulation] : []);
            const visible = items.slice(0, MAX_ITEMS);
            const remaining = items.length - MAX_ITEMS;
            if (items.length === 0) {
              return <p className="text-sm italic text-gray-400">Not provided</p>;
            }
            return (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                  {visible.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                {remaining > 0 && (
                  <p className="text-xs text-gray-400 mt-1 pl-5">+ {remaining} more</p>
                )}
              </div>
            );
          })()}
        </SummarySection>

        {/* Confidential notes recap — amber-themed to match the on-page panel. */}
        <SummarySection title="Confidential notes:">
          {(() => {
            const MAX_ITEMS = 3;
            const items = Array.isArray(confidentialNotes)
              ? confidentialNotes.map(n => n?.text || '').filter(Boolean)
              : (typeof confidentialNotes === 'string' && confidentialNotes.trim() ? [confidentialNotes] : []);
            const visible = items.slice(0, MAX_ITEMS);
            const remaining = items.length - MAX_ITEMS;
            if (items.length === 0) {
              return <p className="text-sm italic text-gray-400">Not provided</p>;
            }
            return (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                  {visible.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                {remaining > 0 && (
                  <p className="text-xs text-gray-400 mt-1 pl-5">+ {remaining} more</p>
                )}
              </div>
            );
          })()}
        </SummarySection>

        <DialogMessage>
          Clicking Generate Report will mark the observation as complete,
          generate all AI paragraphs, and take you to the Documents Dashboard
          to review them.
        </DialogMessage>
      </ModuleCompletionDialog>

      {/* ── Simulated progress dialog while the Cloud Function runs ──────── */}
      <GenerateReportProgressDialog
        reportStage="Behavioural Observation"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
        completionButtonLabel="Go to Documents"
      />
      </div>
    </Layout>
  );
}
